import type { PayloadAction } from '@reduxjs/toolkit';
import type { UpdateUserRolePayload, UpdateUserStatusPayload } from '@/domains/adminUsers/types/AdminUserInformation';
import { AdminUsersAccountsResponse } from '@/domains/adminUsers/types/api/AdminUsersAccountsResponse';
import { EDSFiltersRequestFiltersList } from '@/features/eds/types/api/requests/EDSFiltersRequestBody';
import { HttpServiceResponse } from '@/services/HttpService/types';
import type { RootState } from '@/store/types/RootState';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { EdsDefinitionSections } from '@/features/eds/enums/EdsDefinitionSections';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { EdsFilterEntities } from '@/features/eds/enums/EdsFilterEntities';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import {
  getAdminUsersDataError,
  getAdminUsersSuccess,
  incrementAdminUsersPage,
  searchAdminUsers,
  setLoadingAdminUsersData,
  updateAdminUserRole,
  updateAdminUserRoleSuccess,
  updateAdminUserStatus,
  updateAdminUserStatusSuccess,
} from '@/domains/adminUsers/store/adminUsersSlice';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import SnacksService from '@/services/SnackService/SnackService';

function* updateAdminUserRoleSaga({ payload }: PayloadAction<UpdateUserRolePayload>) {
  try {
    yield put(setSystemLoadingStatus(true));
    yield call(HttpService.patch, `/examiner/admin-panel/${payload.id}/update-account-roles`, {
      roles: payload.userRoles,
    });
    yield put(updateAdminUserRoleSuccess(payload));
    SnacksService.add({
      type: SnackType.Success,
      title: `${payload.userName}'s security role has been updated successfully`,
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    });
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the update',
      content: 'Please try again',
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    });
  } finally {
    yield put(setSystemLoadingStatus(false));
  }
}

function* updateAdminUserStatusSaga({ payload: { id, status } }: PayloadAction<UpdateUserStatusPayload>) {
  try {
    yield put(setSystemLoadingStatus(true));
    const { data } = yield call(HttpService.patch, `/examiner/admin-panel/${id}/update-account-status`, {
      status: status,
    });
    SnacksService.add({
      type: SnackType.Success,
      title: "User's status updated successfully",
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
    yield put(updateAdminUserStatusSuccess({ id, status: data.accountStatus.value }));
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save update, please try again',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  } finally {
    yield put(setSystemLoadingStatus(false));
  }
}

export function* getAdminUsersSaga({ filters }) {
  yield put(setLoadingAdminUsersData(true));

  const pagination = yield select((state: RootState) => state.adminUsersSlice.data.pagination);
  const savedSortingForSending = yield select((state: RootState) => state.adminUsersSlice.savedSortingForSending);

  try {
    const requestUrl = `/filter/${EdsEntityCodes.Account}?${QueryParamsService.constructSearchParamsFromRecord({ page: pagination.page, perPage: pagination.perPage, includes: [EdsEntityCodes.Person, `${EdsEntityCodes.Person}.${EdsEntityCodes.ContactInformation}`, EdsEntityCodes.Roles], sortBy: savedSortingForSending?.propertyName, sortDirection: savedSortingForSending?.sortDirection })}`;

    const { data } = yield call(HttpService.post, requestUrl, filters || [], {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const mappedData = {
      ...data,
      listings: data.listings.map((item) => ({ ...item, roles: (item.roles?.map((role) => role.id) || []).join(',') })),
    };

    const normalizedResponse = yield call(EdsAdapterService.normalize, mappedData.listings, {
      section: EdsDefinitionSections.UsersMaintenance,
    });

    return [normalizedResponse, mappedData.pagination];
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Admin users fetching error',
      content: 'Unable to fetch admin users. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
    yield put(getAdminUsersDataError());
  } finally {
    yield put(setLoadingAdminUsersData(false));
  }
}

function* searchAdminUsersSaga({ type }) {
  yield put(setLoadingAdminUsersData(true));

  const searchQuery = yield select((state: RootState) => state.adminUsersSlice.searchQuery);
  const pagination = yield select((state: RootState) => state.adminUsersSlice.data.pagination);
  const savedFiltersForSending = yield select((state: RootState) => state.adminUsersSlice.savedFiltersForSending);

  try {
    let userIdFilter: EDSFiltersRequestFiltersList = [];

    if (searchQuery) {
      const { data }: HttpServiceResponse<AdminUsersAccountsResponse> = yield call(
        HttpService.get,
        // It's ok to search no more than 10k and no pagination,
        // because in practice this endpoint always returns small number of users
        // and getting more than 10k users from search doesn't make sense at all.
        `/examiner/admin-panel/accounts?page=1&perPage=10000&searchExpression=${searchQuery}`,
      );
      userIdFilter = data.listings.length
        ? [{ propertyName: EdsFilterEntities.Id, values: data.listings.map((listing) => listing.id.toString()) }]
        : [];
    }

    const isSearchEmpty = !userIdFilter[0]?.values.length && searchQuery;
    const [normalizedResponse, newPagination] = isSearchEmpty
      ? [[], { ...pagination, numberOfItems: 0 }]
      : yield call(getAdminUsersSaga, {
          filters: savedFiltersForSending?.filters?.length
            ? [...userIdFilter, ...savedFiltersForSending.filters]
            : userIdFilter,
        });

    yield put(
      getAdminUsersSuccess({
        shouldRewrite: type !== incrementAdminUsersPage.type,

        data: {
          listings: normalizedResponse,
          pagination: {
            ...newPagination,
            isLastPage: newPagination.page * newPagination.perPage >= newPagination.numberOfItems,
          },
        },
      }),
    );
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setLoadingAdminUsersData(false));
  }
}

export function* adminUsersWatcher() {
  yield takeLatest(updateAdminUserStatus.type, updateAdminUserStatusSaga);
  yield takeLatest(updateAdminUserRole.type, updateAdminUserRoleSaga);
  yield takeLatest([searchAdminUsers.type, incrementAdminUsersPage.type], searchAdminUsersSaga);
}
